import { defaultCityRadiusKm } from '@/constants'
import { countryCodeToFlag } from '@/lib'
import { CityLocationMap, SuggestionField } from '@/ui'
import { useMetaLocationSearchCities } from '@jobilla/nexus-sdk'
import { useDebounce } from '@uidotdev/usehooks'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MAX_CITIES = 3

export type AdCampaignCity = {
	id: string
	name: string
	country: { id: string; name: string }
	region: { id: string; name: string } | null
}

type CitySuggestion = {
	id: string
	name: string
	country: { id: string; name: string } | null
	region: { id: string; name: string } | null
	primaryCity?: string | null
}

export type AdCampaignLocationSelectorProps = {
	cities: AdCampaignCity[]
	onChange: (cities: AdCampaignCity[]) => void
	readOnly?: boolean
}

export function AdCampaignLocationSelector({
	cities,
	onChange,
	readOnly = false,
}: AdCampaignLocationSelectorProps) {
	const { t } = useTranslation('editor')
	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search, 300)

	const citiesQuery = useMetaLocationSearchCities(
		{ query: debouncedSearch },
		{
			enabled: debouncedSearch.length > 0,
			retry: false,
		},
	)

	const handleCitiesChange = useCallback(
		(newCities: CitySuggestion[]) => {
			// Enforce max cities
			if (newCities.length > MAX_CITIES) {
				return
			}

			// Dedupe by id and filter out cities without required country field
			const uniqueCities = newCities
				.filter((city, index, array) => array.findIndex((item) => item.id === city.id) === index)
				.filter((city): city is AdCampaignCity => city.country !== null)

			onChange(uniqueCities)
		},
		[onChange],
	)

	if (readOnly) {
		return (
			<div className="flex flex-col gap-2">
				<div className="flex flex-col gap-1">
					<div className="text-sm text-grey-500">
						{t('adCampaign.locations', 'Ad Campaign Locations')}
					</div>
					<div className="text-lg font-semibold text-grey-900">
						{cities.length > 0
							? cities.map((c) => {
								const flag = countryCodeToFlag(c.country.id)
								const parts = [c.name]
								if (c.region?.name && c.region.name !== c.name) {
									parts.push(c.region.name)
								}
								parts.push(c.country.name)
								return `${flag} ${parts.join(', ')}`
							}).join(' | ')
							: t('adCampaign.noLocationsSelected', 'No locations selected')}
					</div>
				</div>
				{cities.length > 0 && (
					<CityLocationMap
						cities={cities.map((city) => ({
							id: city.id,
							name: city.name,
							radius: defaultCityRadiusKm,
							country: city.country,
						}))}
						height={200}
					/>
				)}
			</div>
		)
	}

	const suggestions: CitySuggestion[] = Array.isArray(citiesQuery.data) ? citiesQuery.data : []

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<div className="text-sm text-grey-500">
					{t('adCampaign.locationsWithMax', 'Ad Campaign Locations (max {{max}} cities)', {
						max: MAX_CITIES,
					})}
				</div>
				{cities.length >= MAX_CITIES && (
					<span className="text-sm text-orange-500">
						{t('adCampaign.maxCitiesSelected', 'Maximum cities selected')}
					</span>
				)}
			</div>
			<SuggestionField
				label=""
				values={cities}
				onChange={handleCitiesChange}
				getKey={(city) => city.id}
				search={search}
				onSearchChange={setSearch}
				suggestions={suggestions}
				renderValue={(city) => {
					const cityParts = [city.name]
					if (city.region?.name && city.region.name !== city.name) {
						cityParts.push(city.region.name)
					}
					const flag = city.country?.id ? countryCodeToFlag(city.country.id) : ''
					return (
						<span>
							{flag && <span className="mr-1">{flag}</span>}
							{cityParts.join(', ')}
							{city.country?.name && <span className="text-grey-500">— {city.country.name}</span>}
						</span>
					)
				}}
				renderSuggestion={(city) => {
					const cityParts = [city.name]
					if (city.primaryCity && city.primaryCity !== city.name) {
						cityParts.push(city.primaryCity)
					}
					if (city.region?.name && city.region.name !== city.name) {
						cityParts.push(city.region.name)
					}
					const flag = city.country?.id ? countryCodeToFlag(city.country.id) : ''
					return (
						<span>
							{flag && <span className="mr-1">{flag}</span>}
							{cityParts.join(', ')}
							{city.country?.name && <span className="text-grey-500">— {city.country.name}</span>}
						</span>
					)
				}}
				placeholder={t('adCampaign.searchPlaceholder', 'Search and select cities for ad targeting')}
				isLoading={citiesQuery.isLoading}
				isDisabled={cities.length >= MAX_CITIES}
				emptyMessage={t('adCampaign.noCitiesFound', 'No cities found')}
				hasError={citiesQuery.isError}
				errorMessage={t('adCampaign.searchError', 'Failed to search cities. Please try again.')}
			/>
			{cities.length > 0 && (
				<CityLocationMap
					cities={cities.map((city) => ({
						id: city.id,
						name: city.name,
						radius: defaultCityRadiusKm,
						country: city.country,
					}))}
					height={250}
				/>
			)}
			<p className="text-xs text-grey-400">
				{t(
					'adCampaign.locationHint',
					'These locations will be used for targeting your ad campaign. They can be different from your job location.',
				)}
			</p>
		</div>
	)
}
